import type { CommitmentKpiSchedule } from '@salescore/client-api'
import type { DayOfWeekKey, ScheduleEntry } from '@salescore/features'
import { dayOfWeekKeySchema } from '@salescore/features'
import { Checkbox, Col, Form, Row, Select, Typography } from 'antd'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

export interface CommitmentKpiScheduleFormValues {
  timezone: string
  frequency: 'daily' | 'weekly'
  week: DayOfWeekKey[]
}

const FIXED_TIMEZONE = 'Asia/Tokyo'
const FIXED_HOUR = '17' as const

export const buildCommitmentKpiScheduleEntry = (values: CommitmentKpiScheduleFormValues): ScheduleEntry => {
  const baseMonth = { '-1': { [FIXED_HOUR]: true } }
  if (values.frequency === 'daily') {
    return { timezone: values.timezone, day: { [FIXED_HOUR]: true }, month: baseMonth }
  }
  const week = Object.fromEntries(values.week.map((day) => [day, { [FIXED_HOUR]: true }]))
  return { timezone: values.timezone, week, month: baseMonth }
}

export const buildCommitmentKpiScheduleFormValues = (
  schedule: CommitmentKpiSchedule | null | undefined,
): CommitmentKpiScheduleFormValues => {
  if (schedule === null || schedule === undefined) {
    return { timezone: FIXED_TIMEZONE, frequency: 'weekly', week: ['fri'] }
  }
  const { timezone, day, week } = schedule.entry
  if (day !== undefined) {
    return { timezone, frequency: 'daily', week: [] }
  }
  if (week !== undefined) {
    return {
      timezone,
      frequency: 'weekly',
      week: Object.entries(week)
        .filter(([, v]) => v[FIXED_HOUR] === true)
        .map(([k]) => dayOfWeekKeySchema.parse(k)),
    }
  }
  return { timezone: FIXED_TIMEZONE, frequency: 'weekly', week: ['fri'] }
}

export const CommitmentKpiScheduleForm = ({ fieldNamePrefix }: { fieldNamePrefix: string[] }): ReactElement => {
  const { t } = useTranslation()
  const frequencies: Array<{ value: 'daily' | 'weekly'; label: string }> = [
    { value: 'daily', label: t('毎日') },
    { value: 'weekly', label: t('毎週') },
  ]
  const daysOfWeek: Array<{ value: DayOfWeekKey; label: string }> = [
    { value: 'sun', label: t('日', { context: '曜日' }) },
    { value: 'mon', label: t('月', { context: '曜日' }) },
    { value: 'tue', label: t('火', { context: '曜日' }) },
    { value: 'wed', label: t('水', { context: '曜日' }) },
    { value: 'thu', label: t('木', { context: '曜日' }) },
    { value: 'fri', label: t('金', { context: '曜日' }) },
    { value: 'sat', label: t('土', { context: '曜日' }) },
  ]
  const frequency = Form.useWatch<'daily' | 'weekly' | undefined>([...fieldNamePrefix, 'frequency'])

  const descriptionText =
    frequency === 'daily'
      ? t('毎日17時頃に、ダッシュボードに表示されているユーザーへ進捗がメール通知されます')
      : t('毎週指定した曜日の17時頃に、ダッシュボードに表示されているユーザーへ進捗がメール通知されます')

  return (
    <>
      <Typography.Text className="mb-3 block text-sm text-gray-500">{descriptionText}</Typography.Text>
      <Form.Item label={t('タイムゾーン')} layout="horizontal" className="mb-2">
        <Typography.Text>{`${FIXED_TIMEZONE} (JST)`}</Typography.Text>
      </Form.Item>
      <Form.Item
        name={[...fieldNamePrefix, 'frequency']}
        label={t('通知頻度')}
        layout="horizontal"
        rules={[{ required: true, message: t('通知頻度を選択してください') }]}
        className="mb-2"
      >
        <Select options={frequencies} />
      </Form.Item>
      {frequency === 'weekly' && (
        <Form.Item
          name={[...fieldNamePrefix, 'week']}
          label={t('通知曜日')}
          layout="horizontal"
          required
          className="mb-2"
          rules={[
            {
              type: 'array',
              min: 1,
              message: t('通知曜日を選択してください'),
            },
          ]}
        >
          <Checkbox.Group>
            <Row gutter={28}>
              {daysOfWeek.map((day) => (
                <Col key={day.value} className="text-center">
                  <div className="mb-1">{day.label}</div>
                  <Checkbox value={day.value} aria-label={day.label} />
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </Form.Item>
      )}
      <Form.Item name={[...fieldNamePrefix, 'timezone']} hidden>
        <input />
      </Form.Item>
    </>
  )
}
